import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import {
  catalogExam,
  scoreSummary,
  scoreTrend,
  type MockScore,
  type LocalDate,
  type Instant,
  type UUID,
} from '@hoedokloop/domain';
import { useStudy } from '../features/study/study-context';
import { usePlatform } from '../platform/platform-context';
import { useLocalSetting } from '../features/study/local-settings';
export function ScoresPage() {
  const { exams, subjects } = useStudy();
  const { studyRepository } = usePlatform();
  const [requested, setRequested] = useState('');
  const exam = exams.find((exam) => exam.id === requested) ?? exams[0];
  const [scores, setScores] = useState<MockScore[]>([]);
  const [editing, setEditing] = useState<MockScore>();
  const [title, setTitle] = useState('');
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [values, setValues] = useState<Record<string, string>>({});
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);
  const [deleted, setDeleted] = useState<MockScore>();
  const preset = catalogExam(exam?.presetId);
  const stage = preset?.stages.find((stage) => stage.id === exam?.stage) ?? preset?.stages[0];
  const criteria = useLocalSetting(`pass-criteria:${exam?.id ?? 'none'}`, {
    minimum: stage?.minimum ?? 40,
    average: stage?.average ?? 60,
  });
  const [minimum, setMinimum] = useState('40');
  const [average, setAverage] = useState('60');
  const load = async () => {
    const result = await studyRepository.list('mockScores');
    if (result.ok) setScores(result.value);
    else setError('점수를 열지 못했어요. 다시 시도해 주세요.');
  };
  useEffect(() => {
    void load();
  }, [studyRepository]);
  const ownSubjects = subjects.filter((subject) => subject.examId === exam?.id);
  const ownScores = scores
    .filter((score) => score.examId === exam?.id)
    .sort((a, b) => b.studyDate.localeCompare(a.studyDate));
  const latest = ownScores[0];
  const summary = latest
    ? scoreSummary(latest, criteria.value.minimum, criteria.value.average)
    : undefined;
  const reset = () => {
    setEditing(undefined);
    setTitle('');
    setValues({});
  };
  const save = async () => {
    if (!exam || busy) return;
    const marks = ownSubjects.map((subject) => ({
      subjectId: subject.id,
      score: Number(values[subject.id]),
    }));
    if (
      !title.trim() ||
      !date ||
      date > new Date().toISOString().slice(0, 10) ||
      marks.some(
        (mark) =>
          !values[mark.subjectId]?.trim() ||
          !Number.isFinite(mark.score) ||
          mark.score < 0 ||
          mark.score > 100,
      )
    ) {
      setError('회차 이름·날짜와 모든 과목의 0~100점을 적어 주세요.');
      return;
    }
    setBusy(true);
    const at = new Date().toISOString() as Instant;
    const score: MockScore = {
      id: editing?.id ?? (crypto.randomUUID() as UUID),
      examId: exam.id,
      title: title.trim(),
      studyDate: date as LocalDate,
      scores: marks,
      createdAt: editing?.createdAt ?? at,
      updatedAt: at,
      revision: (editing?.revision ?? 0) + 1,
    };
    const result = await studyRepository.put('mockScores', score);
    setBusy(false);
    if (!result.ok) {
      setError('점수를 저장하지 못했어요. 입력은 그대로예요. 다시 시도해 주세요.');
      return;
    }
    setError(undefined);
    reset();
    await load();
  };
  if (!exam)
    return (
      <div className="pn-page">
        <h1>모의고사 점수</h1>
        <p>시험을 만들면 점수를 남길 수 있어요.</p>
        <Link to="/onboarding">시험 만들기</Link>
      </div>
    );
  return (
    <div className="pn-page">
      <Link to="/report">리포트로</Link>
      <h1>모의고사·기출 점수</h1>
      <label className="pn-field">
        시험
        <select
          value={exam.id}
          onChange={(event) => {
            setRequested(event.target.value);
            reset();
          }}
        >
          {exams.map((item) => (
            <option key={item.id} value={item.id}>
              {item.title}
            </option>
          ))}
        </select>
      </label>
      {summary ? (
        <section className="pn-settings-section">
          <h2>최근 평균 {summary.average.toFixed(1)}점</h2>
          {summary.risks.map((id) => (
            <p key={id} className="pn-form-error">
              {subjects.find((subject) => subject.id === id)?.name} 과락 위험 ·{' '}
              <Link to={`/today-session?subject=${id}`}>오늘 복습하기</Link>
            </p>
          ))}
          {summary.averageRisk ? (
            <p>평균 기준 {criteria.value.average}점까지 더 확인해요.</p>
          ) : null}
          <p>합격을 보장하는 판정이 아니에요. 공식 기준이 우선해요.</p>
          {ownScores.length < 2 ? (
            <p>한 번 더 남기면 추이가 보여요.</p>
          ) : (
            <svg viewBox="0 0 300 120" role="img" aria-label="최근 3회 평균 점수 추이">
              <line x1="0" y1="72" x2="300" y2="72" stroke="#a52f36" strokeDasharray="4" />
              <line x1="0" y1="48" x2="300" y2="48" stroke="#344d3a" strokeDasharray="4" />
              <polyline
                fill="none"
                stroke="#344d3a"
                strokeWidth="3"
                points={scoreTrend(scores, exam.id)
                  .map((point, index) => `${30 + index * 110},${120 - point.average * 1.2}`)
                  .join(' ')}
              />
            </svg>
          )}
        </section>
      ) : (
        <p>기출 한 회를 풀고 점수를 남기면 과락 위험 과목을 알려드려요.</p>
      )}
      {!preset ? (
        <details>
          <summary>내 시험 합격 기준</summary>
          <label>
            과목 최저
            <input
              type="number"
              min="0"
              max="100"
              value={minimum}
              onChange={(event) => setMinimum(event.target.value)}
            />
          </label>
          <label>
            평균
            <input
              type="number"
              min="0"
              max="100"
              value={average}
              onChange={(event) => setAverage(event.target.value)}
            />
          </label>
          <button
            onClick={() => {
              const min = Number(minimum),
                avg = Number(average);
              if (min < 0 || min > 100 || avg < 0 || avg > 100) {
                setError('기준은 0~100점이에요.');
                return;
              }
              void criteria.save({ minimum: min, average: avg });
            }}
          >
            기준 저장
          </button>
        </details>
      ) : (
        <p>
          {preset.notice} <a href={preset.sourceUrl}>공식 기준</a> · 확인 {preset.checkedAt}
        </p>
      )}
      <section className="pn-settings-section">
        <h2>{editing ? '점수 수정' : '점수 남기기'}</h2>
        <label className="pn-field">
          회차 이름
          <input maxLength={120} value={title} onChange={(event) => setTitle(event.target.value)} />
        </label>
        <label className="pn-field">
          푼 날<input type="date" value={date} onChange={(event) => setDate(event.target.value)} />
        </label>
        {ownSubjects.map((subject) => (
          <label className="pn-field" key={subject.id}>
            {subject.name} 점수
            <input
              type="number"
              inputMode="decimal"
              min="0"
              max="100"
              value={values[subject.id] ?? ''}
              onChange={(event) => setValues({ ...values, [subject.id]: event.target.value })}
            />
          </label>
        ))}
        <button className="pn-button" disabled={busy} onClick={() => void save()}>
          {busy ? '저장 중…' : '점수 저장'}
        </button>
        {editing ? (
          <button className="pn-small-button" onClick={reset}>
            수정 취소
          </button>
        ) : null}
      </section>
      {error || criteria.error ? (
        <p role="alert">
          {error || criteria.error} <button onClick={() => void load()}>다시 불러오기</button>
        </p>
      ) : null}
      {ownScores.map((score) => (
        <article className="pn-log-card" key={score.id}>
          <h2>{score.title}</h2>
          <p>
            {score.studyDate} · 평균 {scoreSummary(score).average.toFixed(1)}점
          </p>
          <button
            className="pn-small-button"
            onClick={() => {
              setEditing(score);
              setTitle(score.title);
              setDate(score.studyDate);
              setValues(
                Object.fromEntries(
                  score.scores.map((item) => [item.subjectId, String(item.score)]),
                ),
              );
            }}
          >
            수정
          </button>
          <button
            className="pn-small-button"
            onClick={() =>
              void studyRepository
                .remove('mockScores', score.id, new Date().toISOString() as Instant)
                .then((result) => {
                  if (result.ok) {
                    setDeleted({ ...score, revision: score.revision + 1 });
                    void load();
                  } else setError('삭제하지 못했어요. 다시 시도해 주세요.');
                })
            }
          >
            삭제
          </button>
        </article>
      ))}
      {deleted ? (
        <button
          className="pn-small-button"
          onClick={() =>
            void studyRepository
              .put('mockScores', {
                ...deleted,
                updatedAt: new Date().toISOString() as Instant,
                revision: deleted.revision + 1,
              })
              .then((result) => {
                if (result.ok) {
                  setDeleted(undefined);
                  void load();
                } else setError('되돌리지 못했어요. 다시 시도해 주세요.');
              })
          }
        >
          점수 삭제 되돌리기
        </button>
      ) : null}
    </div>
  );
}
